import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Area, AreaChart, ResponsiveContainer, Tooltip as RTooltip } from "recharts";
import { cn, dateLocale } from "@/lib/utils";

import { translateFr as tfr } from "@/i18n";
// KPI-specific semantics — an increase is not always good.
export const KPI_CHART_CONFIG = {
  relancer: { label: tfr("À relancer"), unitSingular: tfr("relance"), unitPlural: "relances", increaseMeaning: "negative" },
  places: { label: tfr("Placés"), unitSingular: tfr("joueur placé"), unitPlural: tfr("joueurs placés"), increaseMeaning: "positive" },
  discussion: { label: "En discussion", unitSingular: "joueur en discussion", unitPlural: "joueurs en discussion", increaseMeaning: "positive" },
  contacte: { label: tfr("Contacté"), unitSingular: tfr("joueur contacté"), unitPlural: tfr("joueurs contactés"), increaseMeaning: "positive" },
};

export function formatSparkDate(iso, rangeKey) {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  const opts = rangeKey === "7d"
    ? { day: "numeric", month: "long", year: "numeric" }
    : { day: "numeric", month: "short", year: "numeric" };
  return d.toLocaleDateString(dateLocale(), opts);
}

export function formatUnit(value, cfg) {
  const unit = value === 1 ? cfg.unitSingular : cfg.unitPlural;
  return `${value.toLocaleString(dateLocale())} ${unit}`;
}

export function changeMeta(change, increaseMeaning) {
  if (change === null || change === undefined) return null;
  if (change === 0) return { text: tfr("Aucun changement vs. veille"), cls: "text-muted-foreground" };
  const improving = increaseMeaning === "negative" ? change < 0 : change > 0;
  return {
    text: `${change > 0 ? "+" : "−"}${Math.abs(change).toLocaleString(dateLocale())} vs. veille`,
    cls: improving ? "text-success" : "text-warning",
  };
}

export function buildSparkSummary(cfg, data) {
  const values = data.map((p) => p.v.toLocaleString(dateLocale()));
  const list = values.length > 1 ? `${values.slice(0, -1).join(", ")} et ${values[values.length - 1]}` : values[0];
  return `${cfg.label} sur la période : ${list} ${cfg.unitPlural}.`;
}

// Presentational tooltip card (also unit-tested directly).
export function KpiSparkTooltipCard({ point, prevPoint, metric, rangeKey }) {
  const cfg = KPI_CHART_CONFIG[metric] || { label: metric, unitSingular: "", unitPlural: "", increaseMeaning: "positive" };
  const change = prevPoint ? point.v - prevPoint.v : null;
  const cm = changeMeta(change, cfg.increaseMeaning);
  return (
    <div
      role="status"
      data-testid={`spark-tooltip-${metric}`}
      className="chart-tooltip-surface min-w-[150px] max-w-[min(240px,calc(100vw-32px))] rounded-[10px] px-3 py-2.5 pointer-events-none"
    >
      <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[color:var(--chart-tooltip-label)]">{cfg.label}</p>
      <p className="text-[11px] font-medium text-[color:var(--chart-tooltip-muted)] mt-0.5" data-testid="spark-tooltip-date">
        {formatSparkDate(point.d, rangeKey)}
      </p>
      <p className="text-[15px] font-semibold mt-1 tabular-nums" data-testid="spark-tooltip-value">
        {formatUnit(point.v, cfg)}
      </p>
      {cm && (
        <p className={cn("text-xs mt-1 tabular-nums", cm.cls)} data-testid="spark-tooltip-change">{cm.text}</p>
      )}
    </div>
  );
}

// Reports recharts hover state via effect (returns nothing visible).
function TooltipSpy({ active, label, coordinate, onActive }) {
  useEffect(() => {
    if (active && label !== undefined && label !== null) onActive(label, coordinate);
    else onActive(null, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, label, coordinate?.x]);
  return null;
}

function PortalTooltip({ anchor, children }) {
  if (!anchor) return null;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1920;
  const left = Math.min(Math.max(anchor.x, 90), vw - 130);
  const top = Math.max(anchor.y - 10, 12);
  return createPortal(
    <div className="fixed z-[80]" style={{ left, top, transform: "translate(-50%, -100%)" }}>
      {children}
    </div>,
    document.body
  );
}

/**
 * Dismiss lifecycle for chart tooltips: scroll (any container, capture phase),
 * touch move, pointer cancel, outside tap, viewport exit, resize.
 * Listeners are registered only while a tooltip is active and fully cleaned up.
 */
export function useDismissChartTooltip({ active, onDismiss, containerRef }) {
  useEffect(() => {
    if (!active) return undefined;
    const close = () => onDismiss();
    const onPointerDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) onDismiss();
    };
    // capture:true catches scroll events from window AND any nested scroll container
    window.addEventListener("scroll", close, { passive: true, capture: true });
    window.addEventListener("touchmove", close, { passive: true });
    window.addEventListener("pointercancel", close, { passive: true });
    window.addEventListener("resize", close);
    document.addEventListener("pointerdown", onPointerDown, true);
    let io;
    if (typeof IntersectionObserver !== "undefined" && containerRef.current) {
      io = new IntersectionObserver(
        (entries) => entries.forEach((en) => { if (!en.isIntersecting) onDismiss(); }),
        { threshold: 0 }
      );
      io.observe(containerRef.current);
    }
    return () => {
      window.removeEventListener("scroll", close, { capture: true });
      window.removeEventListener("touchmove", close);
      window.removeEventListener("pointercancel", close);
      window.removeEventListener("resize", close);
      document.removeEventListener("pointerdown", onPointerDown, true);
      io?.disconnect();
    };
  }, [active, onDismiss, containerRef]);
}


/**
 * DashboardSparkline — shared interactive KPI sparkline.
 * Hover / tap / keyboard (←→) reveal a portal tooltip that is never clipped.
 * Uses only the real historical points already present in `data` ([{d, v}]).
 */
export function DashboardSparkline({ data, metric, stroke, gradientId, rangeKey, label }) {
  const cfg = KPI_CHART_CONFIG[metric] || { label, unitSingular: "", unitPlural: "", increaseMeaning: "positive" };
  const wrapRef = useRef(null);
  const [active, setActive] = useState(null); // { index, x, y } viewport coords
  // Touch devices: render the tooltip inside the card instead of a viewport-fixed portal.
  const [coarsePointer] = useState(
    () => typeof window !== "undefined" && !!window.matchMedia?.("(pointer: coarse)")?.matches
  );
  const dismiss = useCallback(() => setActive(null), []);

  useDismissChartTooltip({ active: !!active, onDismiss: dismiss, containerRef: wrapRef });

  // Close on data refresh or period change (new fetch → new array identity).
  useEffect(() => { setActive(null); }, [data, rangeKey]);

  const activate = useCallback((index, coordX) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect || index === null || index === undefined || !data[index]) { setActive(null); return; }
    const n = Math.max(data.length - 1, 1);
    const x = rect.left + (coordX ?? rect.width * (index / n));
    setActive({ index, x, y: rect.top });
  }, [data]);

  const onSpy = useCallback((idxLabel, coordinate) => {
    if (idxLabel === null || idxLabel === undefined) { setActive(null); return; }
    const index = typeof idxLabel === "number" ? idxLabel : data.findIndex((p) => p.d === idxLabel);
    activate(index >= 0 && index < data.length ? index : null, coordinate?.x);
  }, [data, activate]);

  const onKeyDown = (e) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") {
      if (e.key === "Escape") setActive(null);
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    const cur = active?.index ?? data.length - 1;
    const next = e.key === "ArrowLeft" ? Math.max(0, cur - 1) : Math.min(data.length - 1, cur + 1);
    activate(next);
  };

  const point = active ? data[active.index] : null;
  const prevPoint = active && active.index > 0 ? data[active.index - 1] : null;

  return (
    <div
      ref={wrapRef}
      role="img"
      tabIndex={0}
      aria-label={buildSparkSummary(cfg, data)}
      data-testid={`spark-${metric}`}
      onFocus={() => activate(active?.index ?? data.length - 1)}
      onBlur={() => setActive(null)}
      onKeyDown={onKeyDown}
      onMouseLeave={() => setActive(null)}
      onClick={(e) => e.stopPropagation()}
      style={{ "--tw-ring-color": `${stroke}59` }}
      className="relative h-full w-full rounded-md outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-2 cursor-crosshair"
    >
      <ResponsiveContainer width="100%" height={40} minWidth={40} initialDimension={{ width: 320, height: 40 }}>
        <AreaChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: 0 }} accessibilityLayer={false}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={stroke} stopOpacity={0.25} />
              <stop offset="100%" stopColor={stroke} stopOpacity={0} />
            </linearGradient>
          </defs>
          <RTooltip
            content={<TooltipSpy onActive={onSpy} />}
            cursor={{ stroke: "var(--chart-cursor)", strokeWidth: 1 }}
            isAnimationActive={false}
          />
          <Area
            type="monotone"
            dataKey="v"
            stroke={stroke}
            strokeWidth={1.5}
            fill={`url(#${gradientId})`}
            isAnimationActive={false}
            dot={false}
            activeDot={active ? { r: 4, strokeWidth: 2, stroke, fill: "var(--chart-dot-fill)", style: { filter: `drop-shadow(0 0 4px ${stroke})` } } : false}
          />
        </AreaChart>
      </ResponsiveContainer>
      {point && (
        <PortalTooltip anchor={active}>
          <KpiSparkTooltipCard point={point} prevPoint={prevPoint} metric={metric} rangeKey={rangeKey} />
        </PortalTooltip>
      )}
    </div>
  );
}
